import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  Divider,
  CircularProgress,
  Chip,
  Alert,
  IconButton,
  Collapse
} from '@mui/material';
import {
  QuestionAnswer as QuestionAnswerIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MarkChatRead as MarkChatReadIcon
} from '@mui/icons-material';
import { useServiceRequestQueries, formatDate, Question } from 'shared-lib';

interface QASectionProps {
  requestId: string;
}

/**
 * QA Section component for service requests - Client view
 * Allows clients to view and answer questions from vendors
 */
export const QASection: React.FC<QASectionProps> = ({ requestId }) => {
  const [answerText, setAnswerText] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);

  // Get hooks for questions
  const { useQuestionsByRequest, useAnswerQuestion } = useServiceRequestQueries();
  
  // Fetch questions for this service request
  const { 
    data: questions = [], 
    isLoading: questionsLoading 
  } = useQuestionsByRequest(requestId);
  
  // Mutation for answering a question
  const { 
    mutateAsync: answerQuestion, 
    isPending: isSubmitting 
  } = useAnswerQuestion();

  // Handle answering a question
  const handleAnswerQuestion = async (questionId: string) => {
    if (!answerText[questionId]?.trim()) {
      setError('Answer cannot be empty');
      return;
    }
    
    try {
      setError(null);
      await answerQuestion({ 
        questionId, 
        answer: answerText[questionId] 
      });
      // Clear the answer text and responding state
      setAnswerText(prev => ({ ...prev, [questionId]: '' }));
      setRespondingTo(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit answer');
    }
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleStartResponding = (questionId: string) => {
    setRespondingTo(questionId);
    if (!answerText[questionId]) {
      setAnswerText(prev => ({ ...prev, [questionId]: '' }));
    }
  };

  const handleCancelResponding = () => {
    setRespondingTo(null);
  };

  // Count unanswered questions
  const unansweredCount = questions.filter(q => !q.answer).length;

  // Sort questions by creation date, newest first
  const sortedQuestions = [...questions].sort((a, b) => 
    b.createdAt.getTime() - a.createdAt.getTime()
  );

  return (
    <Paper
      elevation={1}
      sx={{
        mt: 3,
        borderRadius: '12px',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{ 
          p: 2, 
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer'
        }}
        onClick={toggleExpanded}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <QuestionAnswerIcon sx={{ mr: 1 }} />
          <Typography 
            variant="subtitle1" 
            fontWeight={600} 
            sx={{ color: '#ffffff !important' }}
          >
            Questions from Vendors
          </Typography>
          {unansweredCount > 0 && (
            <Chip 
              label={`${unansweredCount} unanswered`} 
              size="small" 
              sx={{ ml: 1, height: 20, fontSize: '0.7rem', bgcolor: 'error.main', color: 'white' }}
            />
          )}
        </Box>
        <IconButton size="small" sx={{ color: 'white' }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Questions list */}
          {questionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : sortedQuestions.length > 0 ? (
            <List sx={{ width: '100%' }}>
              {sortedQuestions.map((question, index) => (
                <React.Fragment key={question.id}>
                  <ListItem 
                    alignItems="flex-start" 
                    sx={{ 
                      flexDirection: 'column',
                      py: 2
                    }}
                  >
                    <Box sx={{ width: '100%', mb: 1 }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 500,
                          mb: 0.5
                        }}
                      >
                        Q: {question.text}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                      >
                        Asked {formatDate(question.createdAt)}
                      </Typography>
                    </Box>
                    
                    {question.answer ? (
                      <Box 
                        sx={{ 
                          width: '100%',
                          mt: 1,
                          p: 2,
                          bgcolor: 'rgba(76, 175, 80, 0.05)',
                          borderRadius: '8px',
                          border: '1px solid rgba(76, 175, 80, 0.2)'
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mb: 0.5,
                            fontWeight: 500
                          }}
                        >
                          A: {question.answer}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                        >
                          Answered {formatDate(question.answeredAt)}
                        </Typography>
                      </Box>
                    ) : respondingTo === question.id ? (
                      <Box 
                        sx={{ 
                          width: '100%',
                          mt: 1
                        }}
                      >
                        <TextField
                          fullWidth
                          placeholder="Type your answer here"
                          value={answerText[question.id] || ''}
                          onChange={(e) => setAnswerText(prev => ({ ...prev, [question.id]: e.target.value }))}
                          disabled={isSubmitting}
                          multiline
                          rows={2}
                          inputProps={{
                            maxLength: 1000
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                            },
                            mb: 1
                          }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            onClick={handleCancelResponding}
                            disabled={isSubmitting}
                          >
                            Cancel
                          </Button>
                          <Button 
                            variant="contained" 
                            color="primary"
                            onClick={() => handleAnswerQuestion(question.id)}
                            disabled={isSubmitting || !answerText[question.id]?.trim()}
                            startIcon={isSubmitting ? <CircularProgress size={16} /> : <SendIcon />}
                          >
                            Answer
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          mt: 1,
                          display: 'flex',
                          justifyContent: 'flex-end'
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleStartResponding(question.id)}
                          startIcon={<MarkChatReadIcon />}
                          size="small"
                        >
                          Answer this question
                        </Button>
                      </Box>
                    )}
                  </ListItem>
                  {index < sortedQuestions.length - 1 && (
                    <Divider component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No questions have been asked by vendors yet.
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default QASection; 